<template>
  <div>
    <div class="font-bold">{{ t("admin.supervisorOrgIdLabel") }}</div>
    <div class="text-xs">{{ user.supervisorOrgId }}</div>
  </div>

  <v-divider class="my-6" />

  <div class="font-bold mb-4">{{ t("supervisorOrgSettings.admin.dailyBalancesExport") }}</div>
  <div class="flex items-center flex-wrap gap-4">
    <v-text-field
      v-model="exportStartDate"
      type="date"
      :label="t('supervisorOrgSettings.admin.startDate')"
      hide-details="auto"
      style="max-width: 180px"
      :disabled="exportingBalances"
    />
    <v-text-field
      v-model="exportEndDate"
      type="date"
      :label="t('supervisorOrgSettings.admin.endDate')"
      hide-details="auto"
      style="max-width: 180px"
      :disabled="exportingBalances"
    />
    <v-btn
      color="primary"
      variant="outlined"
      :loading="exportingBalances"
      :disabled="!exportStartDate || !exportEndDate || exportingBalances"
      @click="handleExportDailyBalances"
    >
      {{ t("supervisorOrgSettings.admin.exportDailyBalances") }}
    </v-btn>
  </div>

  <v-divider class="my-6" />

  <v-form
    ref="form"
    :disabled="loading"
    :loading="loading"
    validate-on="invalid-input lazy"
    @submit.prevent="save"
  >
    <div class="font-bold mb-4">{{ t("supervisorOrgSettings.contract.title") }}</div>

    <v-radio-group
      v-model="contractType"
      :label="t('supervisorOrgSettings.admin.contractType')"
      color="primary"
      inline
      class="-ml-4"
      :disabled="loading || !contractInfoValid"
    >
      <v-radio label="SignatureAPI" value="signatureApi"></v-radio>
      <v-radio :label="t('common.none')" :value="null"></v-radio>
    </v-radio-group>

    <v-alert
      v-if="!contractInfoValid"
      class="mb-4 -mt-4"
      type="warning"
      color="warning"
      variant="tonal"
      density="compact"
      border
    >
      {{ t("account.settings.contract.missingContractInfo") }}
    </v-alert>

    <div class="v-label mb-2">{{ t("supervisorOrgSettings.contract.defaultContact") }}</div>
    <v-text-field
      v-model.trim="contractSignatureContactName"
      :label="t('supervisorOrgSettings.contract.name')"
      :rules="[contractContactNameRule]"
    ></v-text-field>
    <v-text-field
      v-model.trim="contractSignatureContactEmail"
      :label="t('supervisorOrgSettings.contract.email')"
      :rules="[contractContactEmailRule, validEmail]"
    ></v-text-field>

    <v-divider class="my-6" />

    <div class="font-bold mb-2">{{ t("supervisorOrgSettings.admin.contractTemplates") }}</div>

    <v-table density="compact" class="mb-4">
      <thead>
        <tr>
          <th class="text-left">{{ t("supervisorOrgSettings.admin.contractName") }}</th>
          <th class="text-right">{{ t("common.actions") }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="template in contractTemplates" :key="template.id">
          <td>{{ template.name }}</td>
          <td class="text-right">
            <v-btn
              variant="text"
              color="primary"
              icon="mdi-download"
              size="small"
              :aria-label="t('common.download')"
              :disabled="loading"
              @click="downloadTemplate(template.id)"
            />
            <v-btn
              variant="text"
              color="primary"
              icon="mdi-pencil-outline"
              size="small"
              :aria-label="t('common.edit')"
              :disabled="loading || contractDialogBusy"
              @click="openEditContractDialog(template)"
            />
          </td>
        </tr>
      </tbody>
    </v-table>

    <v-btn
      variant="outlined"
      color="primary"
      prepend-icon="mdi-plus"
      class="mb-4"
      :disabled="loading || contractDialogBusy"
      @click="openAddContractDialog"
    >
      {{ t("supervisorOrgSettings.admin.addContract") }}
    </v-btn>

    <v-divider class="my-6" />

    <div class="font-bold mb-2">{{ t("supervisorOrgSettings.admin.settings") }}</div>
    <v-radio-group
      v-model="defaultOpenBankingProvider"
      :label="t('supervisorOrgSettings.admin.defaultOpenBankingProvider')"
      color="primary"
      inline
      class="mt-2 -ml-4"
    >
      <v-radio
        v-for="provider in openBankingProviders"
        :key="provider"
        :label="provider"
        :value="provider"
      ></v-radio>
    </v-radio-group>

    <v-radio-group
      v-model="defaultProAccountProvider"
      :label="t('supervisorOrgSettings.admin.defaultProAccountProvider')"
      color="primary"
      inline
      class="mt-2 -ml-4"
    >
      <v-radio
        v-for="provider in proAccountProviders"
        :key="provider"
        :label="provider"
        :value="provider"
      ></v-radio>
    </v-radio-group>

    <ProAccountPriceOptionsSelect v-model="proAccountPriceOptions" />

    <v-radio-group
      v-model="subscriptionType"
      :label="t('supervisorOrgSettings.admin.subscriptionType')"
      color="primary"
      inline
      class="mt-4 -ml-4"
    >
      <v-radio label="Stripe" value="stripe"></v-radio>
      <v-radio :label="t('common.none')" :value="null"></v-radio>
    </v-radio-group>

    <v-divider class="my-6" />

    <v-btn
      type="submit"
      color="success"
      :disabled="loading || saving"
      :loading="loading || saving"
      >{{ t("supervisorOrgSettings.contract.save") }}</v-btn
    >

    <div class="font-bold mt-6 mb-4 text-error">{{ t("admin.supervisorOrgs.dangerZone") }}</div>
    <v-btn
      color="error"
      variant="outlined"
      append-icon="mdi-delete"
      :disabled="showDeleteDialog"
      data-testid="delete-supervisor-org-btn"
      @click="showDeleteDialog = true"
    >
      {{ t("admin.supervisorOrgs.delete") }}
    </v-btn>
  </v-form>

  <v-dialog v-model="showContractDialog" max-width="560" persistent>
    <v-card>
      <v-card-title>{{
        editingTemplateId
          ? t("supervisorOrgSettings.admin.editContract")
          : t("supervisorOrgSettings.admin.addContract")
      }}</v-card-title>
      <v-card-text>
        <v-form
          ref="contractDialogForm"
          :disabled="contractDialogBusy"
          validate-on="invalid-input lazy"
          @submit.prevent="handleSaveContract"
        >
          <v-text-field
            v-model.trim="contractDialogName"
            :label="t('supervisorOrgSettings.admin.contractName')"
            :rules="[required]"
            hide-details="auto"
          />
          <v-file-input
            v-model="contractDialogFile"
            class="mt-3"
            variant="outlined"
            prepend-inner-icon="mdi-paperclip"
            :prepend-icon="''"
            hide-details="auto"
            accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            :label="
              editingTemplateId
                ? t('supervisorOrgSettings.admin.editContractFile')
                : t('supervisorOrgSettings.admin.addContractFile')
            "
            :rules="contractDialogFileRules"
            :required="!editingTemplateId"
            :error="!!contractDialogTestResult && contractDialogTestResult !== true"
            @update:model-value="contractDialogTestResult = null"
          />

          <div class="v-label mt-3">{{ t("supervisorOrgSettings.admin.contractContactMode") }}</div>
          <v-radio-group
            v-model="contractDialogUseDefaultContact"
            color="primary"
            inline
            hide-details="auto"
          >
            <v-radio
              :label="t('supervisorOrgSettings.admin.contactDefault')"
              :value="true"
              :disabled="contractDialogBusy || !hasDefaultContact"
              class="mr-6"
            />
            <v-radio :label="t('supervisorOrgSettings.admin.contactCustom')" :value="false" />
          </v-radio-group>

          <div v-if="!contractDialogUseDefaultContact" class="flex flex-col gap-2 mt-2">
            <v-text-field
              v-model.trim="contractDialogContactName"
              :label="t('supervisorOrgSettings.contract.name')"
              :rules="[contractDialogContactNameRule]"
              hide-details="auto"
            />
            <v-text-field
              v-model.trim="contractDialogContactEmail"
              :label="t('supervisorOrgSettings.contract.email')"
              :rules="[contractDialogContactEmailRule, validEmail]"
              hide-details="auto"
            />
          </div>

          <v-alert
            v-if="contractDialogTestResult && contractDialogTestResult !== true"
            class="mt-2"
            type="error"
            color="error"
            variant="tonal"
            density="compact"
            border
          >
            <div v-if="contractDialogTestResult.title" class="font-bold mb-2">
              {{ contractDialogTestResult.title }}
            </div>
            <div v-if="contractDialogTestResult.detail">{{ contractDialogTestResult.detail }}</div>
            <div v-for="resultError in contractDialogTestResult.errors" :key="resultError">
              {{ resultError }}
            </div>
            <v-btn
              :href="contractDialogTestResult.type"
              variant="outlined"
              target="_blank"
              class="mt-2"
              >{{ t("supervisorOrgSettings.admin.moreInfo") }}</v-btn
            >
          </v-alert>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-btn
          v-if="editingTemplateId"
          color="error"
          variant="outlined"
          append-icon="mdi-delete"
          :disabled="contractDialogBusy || contractTemplates.length <= 1"
          :loading="deletingTemplateId === editingTemplateId"
          @click="handleDeleteTemplateFromDialog"
        >
          {{ t("common.delete") }}
        </v-btn>
        <v-spacer />
        <v-btn variant="text" :disabled="contractDialogBusy" @click="closeContractDialog">
          {{ t("common.cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="savingContractDialog"
          :disabled="contractDialogBusy"
          @click="handleSaveContract"
        >
          {{ editingTemplateId ? t("common.save") : t("common.add") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="showDeleteDialog" max-width="500" :persistent="deletingSupervisorOrg">
    <v-card>
      <v-card-title>{{ t("admin.supervisorOrgs.confirmDeleteTitle") }}</v-card-title>
      <v-card-text>
        {{ t("admin.supervisorOrgs.confirmDeleteMessage", { name: supervisorOrg?.name }) }}
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn :disabled="deletingSupervisorOrg" @click="showDeleteDialog = false">{{
          t("common.close")
        }}</v-btn>
        <v-btn
          color="error"
          variant="flat"
          :loading="deletingSupervisorOrg"
          :disabled="deletingSupervisorOrg"
          data-testid="delete-supervisor-org-confirm-btn"
          @click="handleDeleteSupervisorOrg"
        >
          {{ t("admin.supervisorOrgs.delete") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import ProAccountPriceOptionsSelect from "@/components/ProAccountPriceOptionsSelect.vue";
import { areSupervisorOrgContractInfoValid } from "@/composables/useContract";
import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { useSupervisorOrg } from "@/composables/useSupervisorOrg";
import { call, db } from "@/firebase";
import { useToast } from "@/store";
import { useUserStore } from "@/store/user";
import type { OpenBankingAccountType, ProAccountAccountType } from "@aircap/common/account";
import type { CallablesMap } from "@aircap/common/callables";
import {
  getContractTemplateFilePath,
  getContractTemplateTempFilePath,
} from "@aircap/common/storage";
import {
  contractTemplatesFullPath,
  type ContractSignatureContact,
  type ContractTemplate,
  type SupervisorOrg,
} from "@aircap/common/supervisorOrg";
import { captureException } from "@sentry/vue";
import {
  collection,
  deleteField,
  doc,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useFirebaseStorage } from "vuefire";
import { VForm } from "vuetify/components";

const maxFileSizeMB = 32;
const { showSuccessToast, showErrorToast } = useToast();
const { t } = useI18n();
const router = useRouter();
const storage = useFirebaseStorage();
const user = useUserStore();
const { required, requiredFile, validEmail, maxFileSize } = useRules();

const saving = ref(false);
const contractSignatureContactName = ref<string | undefined>();
const contractSignatureContactEmail = ref<string | undefined>();
const contractDialogName = ref("");
const contractDialogFile = ref<File | null>(null);
const contractDialogUseDefaultContact = ref(true);
const contractDialogContactName = ref("");
const contractDialogContactEmail = ref("");
const contractDialogTestResult = ref<CallablesMap["testContractTemplate"]["response"] | null>(null);
const showContractDialog = ref(false);
const editingTemplateId = ref<string | null>(null);
const savingContractDialog = ref(false);
const contractDialogForm = ref<InstanceType<typeof VForm> | null>(null);
const deletingTemplateId = ref<string | null>(null);
const contractDialogBusy = computed(() => savingContractDialog.value || !!deletingTemplateId.value);
const defaultOpenBankingProvider = ref<OpenBankingAccountType>("powens");
const defaultProAccountProvider = ref<ProAccountAccountType>("swan");
const proAccountPriceOptions = ref<number[]>([]);
const subscriptionType = ref<"stripe" | null>(null);
const contractType = ref<"signatureApi" | null>(null);
const showDeleteDialog = ref(false);
const deletingSupervisorOrg = ref(false);

const exportStartDate = ref<string>("");
const exportEndDate = ref<string>("");
const exportingBalances = ref(false);

const handleExportDailyBalances = async () => {
  if (!user.supervisorOrgId || !exportStartDate.value || !exportEndDate.value) return;

  try {
    console.log("Exporting daily balances...");
    exportingBalances.value = true;
    const { excel, supervisorOrgName } = await call("exportDailyBalances")({
      supervisorOrgId: user.supervisorOrgId,
      startDate: exportStartDate.value,
      endDate: exportEndDate.value,
    });

    const byteCharacters = atob(excel);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${supervisorOrgName}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (cause) {
    const error = new Error("Error exporting daily balances", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    exportingBalances.value = false;
  }
};

const form = ref<InstanceType<typeof VForm> | null>(null);

const { supervisorOrgRef, supervisorOrg, loadingSupervisorOrg } = useSupervisorOrg();

const contractTemplatesRef = computed(() =>
  user.supervisorOrgId
    ? query(
        collection<ContractTemplate>(db, contractTemplatesFullPath(user.supervisorOrgId)),
        orderBy<ContractTemplate>("createdAt", "asc"),
      )
    : null,
);
const { data: contractTemplates, pending: loadingContractTemplates } =
  useCollection<ContractTemplate>(contractTemplatesRef);

const contractInfoValid = computed(() =>
  areSupervisorOrgContractInfoValid(supervisorOrg.value, contractTemplates.value),
);

const loading = computed(
  () => loadingSupervisorOrg.value || loadingContractTemplates.value || saving.value,
);

const openBankingProviders = ["bridge", "powens", "gcl"] as const;
const proAccountProviders = ["swan", "treezor"] as const;

const hasDefaultContact = computed(
  () =>
    !!contractSignatureContactName.value?.trim() && !!contractSignatureContactEmail.value?.trim(),
);

watch(supervisorOrg, (supervisorOrg) => {
  if (!supervisorOrg) return;

  contractSignatureContactName.value = supervisorOrg.contractSignatureContact?.name;
  contractSignatureContactEmail.value = supervisorOrg.contractSignatureContact?.email;
  defaultOpenBankingProvider.value = supervisorOrg.defaultOpenBankingProvider ?? "powens";
  defaultProAccountProvider.value = supervisorOrg.defaultProAccountProvider ?? "swan";
  proAccountPriceOptions.value = supervisorOrg.proAccountPriceOptions ?? [];
  subscriptionType.value = supervisorOrg.subscriptionType ?? null;
  contractType.value = supervisorOrg.contractType ?? null;
});

watch(hasDefaultContact, (hasContact) => {
  if (!hasContact && showContractDialog.value) {
    contractDialogUseDefaultContact.value = false;
  }
});

const contractContactNameRule = (v: string | undefined) => {
  const hasName = !!v?.trim();
  const hasEmail = !!contractSignatureContactEmail.value?.trim();
  if (!hasEmail && !hasName) return true;
  return required(v);
};

const contractContactEmailRule = (v: string | undefined) => {
  const hasName = !!contractSignatureContactName.value?.trim();
  const hasEmail = !!v?.trim();
  if (!hasName && !hasEmail) return true;
  return required(v);
};

const contractDialogContactNameRule = (v: string | undefined) => {
  if (contractDialogUseDefaultContact.value) return true;
  return required(v);
};

const contractDialogContactEmailRule = (v: string | undefined) => {
  if (contractDialogUseDefaultContact.value) return true;
  return required(v);
};

const contractDialogFileRules = computed(() =>
  editingTemplateId.value
    ? [maxFileSize(maxFileSizeMB)]
    : [requiredFile, maxFileSize(maxFileSizeMB)],
);

const getContractDialogContact = (): ContractSignatureContact | null | undefined => {
  if (contractDialogUseDefaultContact.value) return null;

  const name = contractDialogContactName.value.trim();
  const email = contractDialogContactEmail.value.trim();
  if (!name || !email) return undefined;

  return { name, email };
};

const resetContractDialog = () => {
  contractDialogName.value = "";
  contractDialogFile.value = null;
  contractDialogUseDefaultContact.value = hasDefaultContact.value;
  contractDialogContactName.value = "";
  contractDialogContactEmail.value = "";
  contractDialogTestResult.value = null;
  editingTemplateId.value = null;
};

const closeContractDialog = () => {
  showContractDialog.value = false;
  resetContractDialog();
};

const downloadTemplate = async (templateId: string) => {
  if (!user.supervisorOrgId) return;

  try {
    const path = getContractTemplateFilePath({ supervisorOrgId: user.supervisorOrgId, templateId });
    const url = await getDownloadURL(storageRef(storage, path));
    window.location.assign(url);
  } catch (cause) {
    const error = new Error("Error downloading Swan contract template", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  }
};

const uploadTemplateFile = async ({
  supervisorOrgId,
  templateId,
  file,
}: {
  supervisorOrgId: string;
  templateId: string;
  file: File;
}) => {
  const tempRef = storageRef(
    storage,
    getContractTemplateTempFilePath({ supervisorOrgId, templateId }),
  );
  await uploadBytes(tempRef, file);

  const testResult = await call("testContractTemplate")({ supervisorOrgId, templateId });
  if (testResult !== true) {
    return testResult;
  }

  const finalRef = storageRef(
    storage,
    getContractTemplateFilePath({ supervisorOrgId, templateId }),
  );
  await uploadBytes(finalRef, file, {
    contentDisposition: `inline; filename="${file.name}"`,
    customMetadata: { name: file.name },
  });

  return true;
};

const openAddContractDialog = () => {
  resetContractDialog();
  showContractDialog.value = true;
};

const openEditContractDialog = (template: ContractTemplate & { id: string }) => {
  editingTemplateId.value = template.id;
  contractDialogName.value = template.name;
  contractDialogFile.value = null;
  contractDialogUseDefaultContact.value = template.contractSignatureContact
    ? false
    : hasDefaultContact.value;
  contractDialogContactName.value = template.contractSignatureContact?.name ?? "";
  contractDialogContactEmail.value = template.contractSignatureContact?.email ?? "";
  contractDialogTestResult.value = null;
  showContractDialog.value = true;
};

const handleSaveContract = async () => {
  if (!contractDialogForm.value) return;
  if (!supervisorOrgRef.value) return;
  if (contractDialogBusy.value) return;

  savingContractDialog.value = true;
  try {
    const formValidation = await contractDialogForm.value.validate();
    if (!formValidation.valid) return;
    if (!editingTemplateId.value && !contractDialogFile.value) return;

    const contactUpdate = getContractDialogContact();
    if (contactUpdate === undefined) return;

    const templateDocRef = editingTemplateId.value
      ? doc<ContractTemplate>(
          db,
          contractTemplatesFullPath(supervisorOrgRef.value.id),
          editingTemplateId.value,
        )
      : doc(collection<ContractTemplate>(db, contractTemplatesFullPath(supervisorOrgRef.value.id)));

    if (contractDialogFile.value) {
      contractDialogTestResult.value = await uploadTemplateFile({
        supervisorOrgId: supervisorOrgRef.value.id,
        templateId: templateDocRef.id,
        file: contractDialogFile.value,
      });
      if (contractDialogTestResult.value !== true) {
        return;
      }
    }

    if (editingTemplateId.value) {
      const updatePayload: {
        name: string;
        contractSignatureContact?: ContractSignatureContact | ReturnType<typeof deleteField>;
      } = {
        name: contractDialogName.value.trim(),
      };

      if (contactUpdate === null) {
        updatePayload.contractSignatureContact = deleteField();
      } else {
        updatePayload.contractSignatureContact = contactUpdate;
      }

      await updateDoc(templateDocRef, updatePayload);
    } else {
      await setDoc(templateDocRef, {
        name: contractDialogName.value.trim(),
        createdAt: serverTimestamp(),
        ...(contactUpdate ? { contractSignatureContact: contactUpdate } : {}),
      });
    }

    closeContractDialog();
    showSuccessToast();
  } catch (cause) {
    const error = new Error(
      editingTemplateId.value
        ? "Error updating Swan contract template"
        : "Error adding Swan contract template",
      { cause },
    );
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    savingContractDialog.value = false;
  }
};

const handleDeleteTemplateFromDialog = async () => {
  if (!user.supervisorOrgId || !editingTemplateId.value) return;
  if (contractDialogBusy.value) return;

  try {
    deletingTemplateId.value = editingTemplateId.value;
    await call("deleteContractTemplate")({
      supervisorOrgId: user.supervisorOrgId,
      templateId: editingTemplateId.value,
    });
    closeContractDialog();
    showSuccessToast();
  } catch (cause) {
    const error = new Error("Error deleting Swan contract template", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(t("supervisorOrgSettings.admin.deleteContractTemplateError"));
  } finally {
    deletingTemplateId.value = null;
  }
};

const save = async () => {
  if (!form.value) return;
  if (loading.value) return;
  if (saving.value) return;
  if (!supervisorOrgRef.value) return;

  const formValidation = await form.value.validate();
  if (!formValidation.valid) return;

  try {
    console.log("Saving admin settings...");
    saving.value = true;

    const updateData: {
      contractSignatureContact?: { name: string; email: string } | ReturnType<typeof deleteField>;
      defaultOpenBankingProvider: OpenBankingAccountType;
      defaultProAccountProvider: ProAccountAccountType;
      proAccountPriceOptions: number[];
      contractType: SupervisorOrg["contractType"];
      subscriptionType: SupervisorOrg["subscriptionType"];
    } = {
      defaultOpenBankingProvider: defaultOpenBankingProvider.value,
      defaultProAccountProvider: defaultProAccountProvider.value,
      proAccountPriceOptions: proAccountPriceOptions.value,
      contractType: contractType.value,
      subscriptionType: subscriptionType.value,
    };

    if (contractSignatureContactName.value && contractSignatureContactEmail.value) {
      updateData.contractSignatureContact = {
        name: contractSignatureContactName.value,
        email: contractSignatureContactEmail.value,
      };
    } else if (contractSignatureContactName.value || contractSignatureContactEmail.value) {
      updateData.contractSignatureContact = deleteField();
    }

    await updateDoc(supervisorOrgRef.value, updateData);

    showSuccessToast();
  } catch (cause) {
    const error = new Error("Error updating supervisor org", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    saving.value = false;
  }
};

const handleDeleteSupervisorOrg = async () => {
  if (!user.supervisorOrgId) return;

  try {
    console.log("Deleting supervisorOrg...");
    deletingSupervisorOrg.value = true;
    await call("deleteSupervisorOrg")({ supervisorOrgId: user.supervisorOrgId });

    showSuccessToast();
    user.setAdminSupervisorOrgId(null);
    await router.push({ name: "AdminSupervisorOrgs" });
  } catch (cause) {
    const error = new Error("Error deleting supervisor org", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    deletingSupervisorOrg.value = false;
  }
};
</script>
